import { type NotificationReason, useNotificationControllerSendNotification } from 'korint-sdk';
import { Send } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../forms';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  useToast,
} from '../../ui';
import { Stack } from '@folio/components/ui/flex';

type SendNotificationDialogProps = Readonly<{
  policyId: string;
  reasons: ReadonlyArray<NotificationReason>;
}>;

export function SendNotificationDialog({ policyId, reasons }: SendNotificationDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<NotificationReason>(reasons[0]);

  const { mutate: sendNotification, isPending } = useNotificationControllerSendNotification({
    mutation: {
      onSuccess: () => {
        toast({ title: t('notifications.send.toast.success') });
        setOpen(false);
      },
      onError: () => {
        toast({ variant: 'destructive', title: t('notifications.send.toast.error') });
      },
    },
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    sendNotification({ data: { type: 'ERE', policyId, reason } });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="default" size="default">
          <Send className="h-4 w-4 mr-2" />
          {t('notifications.send.button')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('notifications.send.dialogTitle')}</DialogTitle>
        </DialogHeader>
        <Stack asChild>
          <form onSubmit={handleSubmit}>
            <Stack gap={2}>
              <label htmlFor="notification-reason" className="text-sm font-medium">
                {t('notifications.send.typeLabel')}
              </label>
              <Select
                value={reason}
                onValueChange={value => setReason(value as NotificationReason)}
              >
                <SelectTrigger id="notification-reason">
                  <SelectValue placeholder={t('notifications.send.typePlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {reasons.map(sendableReason => (
                    <SelectItem key={sendableReason} value={sendableReason}>
                      {t(`notifications.reasons.${sendableReason}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Stack>
            <DialogFooter>
              <Button type="submit" disabled={isPending}>
                {isPending ? t('notifications.send.submitting') : t('notifications.send.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
